import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { RoleOption } from 'shared/types/roles';
import { NewSubTask, NewTask, Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

import { dateToFirestoreTimestamp } from './converters/date-to-firestore-timestamp';
import { newTaskConverter } from './converters/task';
import {
  BatchDocuments,
  CollectionOptions,
  getAuthentication,
  getCollection,
  getDatabase,
} from './helpers';

export const createTaskWithSubTasks = async (
  task: NewTask,
  subTasks: NewSubTask[],
) => {
  // Get the currently authenticated user
  const user = (await getAuthentication()).currentUser;

  if (!user) {
    throw Error('unauthenticated');
  }

  const taskCollection = getCollection(CollectionOptions.Tasks);
  const parentDocRef = doc(taskCollection);
  const mainTask = {
    ...(dateToFirestoreTimestamp({
      ...newTaskConverter(task),
      id: parentDocRef.id,
    }) as unknown as Task),
    updatedAt: serverTimestamp() as unknown as Timestamp,
    roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
  };

  // no subtasks mean only 1 task is created, so we can use setDoc directly
  if (!subTasks.length) {
    setDoc(parentDocRef, mainTask);
    return mainTask;
  }

  // create ids for the tasks
  const subTasksWithIds = subTasks.map((subTask) => {
    const docRef = doc(taskCollection);
    return {
      task: {
        ...dateToFirestoreTimestamp({
          ...newTaskConverter(subTask as unknown as NewTask),
          id: docRef.id,
        }),
        updatedAt: serverTimestamp() as unknown as Timestamp,
        parentIds: [parentDocRef.id],
        roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
      },
      docRef,
    };
  });

  const batchDocuments = new BatchDocuments(getDatabase());
  // add the childIds to the main task
  mainTask.childIds = subTasksWithIds.map(({ docRef }) => docRef.id);
  // add parent task
  batchDocuments.addDocument({
    data: mainTask,
    ref: parentDocRef,
  });

  subTasksWithIds.forEach(({ task, docRef }) => {
    batchDocuments.addDocument({ data: task, ref: docRef });
  });

  batchDocuments.commit();

  return mainTask;
};
